import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Button,
  Form,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
  Modal,
  Badge,
  Accordion
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import ConfirmModal from "../components/ConfirmModal";

const Financials = () => {
  const { user } = useAuth();
  const { notify } = useNotification();
  const [financials, setFinancials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFinancial, setNewFinancial] = useState({
    title: "",
    description: "",
    date: "",
    expectedAmount: "",
  });
  const [confirmState, setConfirmState] = useState({ show: false, financialId: null });

  useEffect(() => {
    fetchFinancials();
  }, []);

  const fetchFinancials = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/financials`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFinancials(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching financial records:", error);
      setError("Failed to load financial records. Please try again.");
      setFinancials([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFinancial = async () => {
    try {
      if (!newFinancial.title || !newFinancial.date || !newFinancial.expectedAmount) {
        notify("Please fill in all required fields", "warning");
        return;
      }

      const dataToSend = {
        title: newFinancial.title.trim(),
        description: newFinancial.description.trim(),
        date: newFinancial.date,
        expectedAmount: parseFloat(newFinancial.expectedAmount)
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/financials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        setNewFinancial({ title: "", description: "", date: "", expectedAmount: "" });
        setShowCreateModal(false);
        fetchFinancials();
        notify("Financial occurrence created successfully!", "success");
      } else {
        const errorData = await response.json();
        notify(errorData.message || "Failed to create occurrence.", "danger");
      }
    } catch (error) {
      console.error("Error creating financial record:", error);
      notify("Failed to create financial occurrence.", "danger");
    }
  };

  const togglePaymentStatus = async (financialId, userId, currentStatus) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        notify("No authentication token. Please log in again.", "danger");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/financials/${financialId}/payment`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          userId,
          hasPaid: !currentStatus
        }),
      });

      if (response.ok) {
        fetchFinancials();
        notify("Payment status updated successfully!", "success");
      } else {
        if (response.status === 401) {
          notify("Session expired. Please log in again.", "warning");
        } else {
          notify("Failed to update payment status.", "danger");
        }
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
      notify("Failed to update payment status. Please check your connection.", "danger");
    }
  };

  const deleteFinancial = async (financialId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/financials/${financialId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (response.ok) {
        fetchFinancials();
        notify("Financial occurrence deleted successfully!", "success");
      } else {
        notify("Failed to delete financial occurrence.", "danger");
      }
    } catch (error) {
      console.error("Error deleting financial record:", error);
      notify("Failed to delete financial occurrence.", "danger");
    }
  };

  const handleDeleteClick = (financialId) => {
    setConfirmState({ show: true, financialId });
  };

  const handleConfirmDelete = () => {
    deleteFinancial(confirmState.financialId);
    setConfirmState({ show: false, financialId: null });
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading financial records...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h2 className="text-center fw-bold mb-4 text-primary">Financial Contributions</h2>
      <p className="text-center text-muted mb-4">
        Track member contributions for bereavements and other occurrences.
        <br />
        <small>Click on any occurrence below to expand and view detailed payment status.</small>
      </p>

      {error && <Alert variant="danger" className="text-center">{error}</Alert>}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="shadow-sm border-0 text-center stat-card stat-card-primary">
            <Card.Body>
              <h3 className="text-primary">{financials.length}</h3>
              <p className="text-muted">Total Occurrences</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 text-center stat-card stat-card-warning">
            <Card.Body>
              <h3 className="text-warning">
                {financials.filter(financial => {
                  const paidCount = financial.allMembers?.filter(m => m.hasPaid).length || 0;
                  const totalMembers = financial.allMembers?.length || 0;
                  return paidCount < totalMembers;
                }).length}
              </h3>
              <p className="text-muted">Pending Collections</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 text-center stat-card stat-card-success">
            <Card.Body>
              <h3 className="text-success">
                {financials.filter(financial => {
                  const paidCount = financial.allMembers?.filter(m => m.hasPaid).length || 0;
                  const totalMembers = financial.allMembers?.length || 0;
                  return paidCount === totalMembers && totalMembers > 0;
                }).length}
              </h3>
              <p className="text-muted">Fully Collected</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Admin Add Button */}
      {user.role === "admin" && (
        <div className="text-center mb-4">
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            Add Financial Occurrence
          </Button>
        </div>
      )}

      {/* Financial Occurrences - Collapsible */}
      {financials.length > 0 ? (
        <Accordion>
          {financials.map((financial, index) => {
            const paidCount = financial.allMembers?.filter(m => m.hasPaid).length || 0;
            const totalMembers = financial.allMembers?.length || 0;
            const percentage = totalMembers > 0 ? Math.round((paidCount / totalMembers) * 100) : 0;

            return (
              <Accordion.Item eventKey={index.toString()} key={financial._id}>
                <Accordion.Header>
                  <div className="d-flex justify-content-between w-100 align-items-center me-3">
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1 fw-bold">{financial.title}</h6>
                          <small className="text-muted">
                            {new Date(financial.date).toLocaleDateString()} |
                            ${financial.expectedAmount} expected
                          </small>
                          {financial.description && (
                            <div className="mt-1">
                              <small className="text-muted">{financial.description}</small>
                            </div>
                          )}
                        </div>
                        <div className="text-end ms-3">
                          <Badge
                            bg={percentage === 100 ? "success" : percentage >= 50 ? "warning" : "danger"}
                            className="fs-6 mb-1"
                          >
                            {paidCount}/{totalMembers}
                          </Badge>
                          <div>
                            <small className="text-muted">{percentage}% Complete</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 className="mb-0 text-dark">Member Payment Status</h6>
                    {user.role === "admin" && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(financial._id);
                        }}
                        className="shadow-sm"
                      >
                        Delete Occurrence
                      </Button>
                    )}
                  </div>

                  <div className="table-responsive" onClick={(e) => e.stopPropagation()}>
                    <Table className="mb-0 shadow-sm table-hover" hover>
                      <thead>
                        <tr>
                          <th className="py-3 px-4">Member</th>
                          <th className="py-3 px-3 text-center">Status</th>
                          <th className="py-3 px-3">Date Paid</th>
                          {user.role === "admin" && <th className="py-3 px-3 text-center">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {financial.allMembers?.map((member, idx) => (
                          <tr
                            key={member.userId}
                            style={{borderTop: idx === 0 ? "2px solid #dee2e6" : "1px solid #dee2e6"}}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <td className="border-0 py-3 px-4">
                              <div>
                                <div className="fw-bold text-dark" style={{fontSize: "0.95rem"}}>{member.name}</div>
                                <small className="text-muted">{member.email}</small>
                              </div>
                            </td>
                            <td className="border-0 py-3 px-3 text-center">
                              {member.hasPaid ? (
                                <Badge bg="success" className="px-3 py-2" style={{fontSize: "0.8rem"}}>Paid</Badge>
                              ) : (
                                <Badge bg="danger" className="px-3 py-2" style={{fontSize: "0.8rem"}}>Unpaid</Badge>
                              )}
                            </td>
                            <td className="border-0 py-3 px-3" style={{fontSize: "0.9rem"}}>
                              {member.paidDate
                                ? <span className="text-dark">{new Date(member.paidDate).toLocaleDateString()}</span>
                                : <span className="text-muted">-</span>
                              }
                            </td>
                            {user.role === "admin" && (
                              <td
                                className="border-0 py-3 px-3 text-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                              >
                                <Button
                                  variant={member.hasPaid ? "outline-danger" : "outline-success"}
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    e.nativeEvent.stopImmediatePropagation();
                                    togglePaymentStatus(financial._id, member.userId, member.hasPaid);
                                  }}
                                  className="shadow-sm px-3"
                                  style={{fontSize: "0.8rem"}}
                                >
                                  {member.hasPaid ? "Mark Unpaid" : "Mark Paid"}
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Payment Summary */}
                  <div className="mt-4 p-4 rounded section-light">
                    <h6 className="text-dark mb-3 fw-bold">Collection Summary</h6>
                    <Row>
                      <Col md={3}>
                        <div className="text-center p-2">
                          <small className="text-muted d-block">Expected per Member</small>
                          <div className="fw-bold text-primary h5 mb-0">${financial.expectedAmount}</div>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center p-2">
                          <small className="text-muted d-block">Total Collected</small>
                          <div className="fw-bold text-success h5 mb-0">${(paidCount * financial.expectedAmount).toFixed(0)}</div>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center p-2">
                          <small className="text-muted d-block">Still Pending</small>
                          <div className="fw-bold text-warning h5 mb-0">${((totalMembers - paidCount) * financial.expectedAmount).toFixed(0)}</div>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center p-2">
                          <small className="text-muted d-block">Completion</small>
                          <div className="fw-bold text-info h5 mb-0">{percentage}%</div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      ) : (
        <Card className="text-center py-5">
          <Card.Body>
            <h5 className="text-muted">No financial occurrences yet</h5>
            <p className="text-muted">
              {user.role === "admin"
                ? "Create the first financial occurrence to start tracking contributions"
                : "Financial contributions will appear here once created by admins"
              }
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Create Financial Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create Financial Occurrence</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Bereavement - John Doe"
                value={newFinancial.title}
                onChange={(e) => setNewFinancial({...newFinancial, title: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Additional details about this occurrence..."
                value={newFinancial.description}
                onChange={(e) => setNewFinancial({...newFinancial, description: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={newFinancial.date}
                onChange={(e) => setNewFinancial({...newFinancial, date: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Expected Amount ($)</Form.Label>
              <Form.Control
                type="number"
                placeholder="50"
                value={newFinancial.expectedAmount}
                onChange={(e) => setNewFinancial({...newFinancial, expectedAmount: e.target.value})}
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateFinancial}
            disabled={!newFinancial.title || !newFinancial.date || !newFinancial.expectedAmount}
          >
            Create Occurrence
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={confirmState.show}
        title="Delete Financial Occurrence"
        message="Are you sure you want to delete this financial occurrence? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmState({ show: false, financialId: null })}
      />
    </Container>
  );
};

export default Financials;
